import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Flex,
  Input,
  List,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import PropTypes from 'prop-types';
import { notificationTimeout, notificationType } from '../common/constants';
import notValid from '../common/notValid';
import { db } from '../firebase';

const { Text, Title } = Typography;

// Roles offered to the docTypes editor.  Mirrors the BE
// `defaultDocTypesFor()` switch in functions/submitAccountRequest.js.
const ROLES = [
  { value: 'owner',        label: 'Owner' },
  { value: 'masterTenant', label: 'Master Tenant' },
  { value: 'tenant',       label: 'Tenant' },
  { value: 'admin',        label: 'Admin' },
  { value: 'subAdmin',     label: 'Sub-Admin' },
];

// BE defaults, kept in sync with functions/submitAccountRequest.js
// defaultDocTypesFor().  Shown as a "Use defaults" button to seed.
const DEFAULT_DOC_TYPES = {
  owner: [
    { key: 'propertyTitle',    label: 'Property title', required: true },
    { key: 'nric',             label: 'NRIC / ID',      required: false },
  ],
  masterTenant: [
    { key: 'tenancyAgreement', label: 'Tenancy agreement', required: true },
    { key: 'nric',             label: 'NRIC / ID',         required: false },
  ],
  tenant: [
    { key: 'tenancyAgreement', label: 'Tenancy agreement', required: true },
    { key: 'nric',             label: 'NRIC / ID',         required: false },
  ],
  admin:    [],
  subAdmin: [],
};

// D24 P3 (2026-05-14) — Super Admin tab for managing canned rejection reasons
// (and, in future, document types) used by the in-app account application
// flow. Stored at sites/{siteId}.siteDetail.configuration.rejectionReasons
// as an array of strings.
//
// MVP: list + add + delete + save. No drag-reorder, no per-role filtering.
// Reject modal on web doesn't yet consume this list as chips — that's a
// follow-up. Storing the data is the prerequisite.
export default function SiteConfigAccountApplication({ siteId }) {
  const [reasons, setReasons] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // docTypes: { owner: [{key,label,required}, ...], tenant: [...], ... }
  // docsRequired: { owner: bool, tenant: bool, ... } — default missing => true
  const [docTypes, setDocTypes] = useState({});
  const [docsRequired, setDocsRequired] = useState({});
  const [selectedRole, setSelectedRole] = useState('owner');

  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeError, typeSuccess, typeWarning } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const load = useCallback(
    async (nameSite) => {
      if (notValid(nameSite)) return;
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'sites', nameSite));
        const cfg = snap.exists() ? (snap.data().siteDetail?.configuration ?? {}) : {};
        const list = cfg.rejectionReasons ?? [];
        setReasons(Array.isArray(list) ? list : []);
        setDocTypes(
          cfg.accountApplicationDocTypes && typeof cfg.accountApplicationDocTypes === 'object'
            ? cfg.accountApplicationDocTypes
            : {},
        );
        setDocsRequired(
          cfg.accountApplicationDocsRequired && typeof cfg.accountApplicationDocsRequired === 'object'
            ? cfg.accountApplicationDocsRequired
            : {},
        );
        setDirty(false);
      } catch (error) {
        console.error('-- Error loading account-application config:', error);
        openMessage('Error loading config', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  useEffect(() => {
    load(siteId);
  }, [load, siteId]);

  const handleAdd = () => {
    const value = draft.trim();
    if (!value) {
      openMessage('Enter a reason first', timeoutWarning, typeWarning);
      return;
    }
    if (reasons.includes(value)) {
      openMessage('That reason already exists', timeoutWarning, typeWarning);
      return;
    }
    setReasons([...reasons, value]);
    setDraft('');
    setDirty(true);
  };

  const handleRemove = (index) => {
    setReasons(reasons.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleSave = async () => {
    if (notValid(siteId)) return;
    // Validate docTypes integrity before writing — key non-empty + unique
    // per role, label non-empty.  Server-side validation runs too but a
    // pre-flight here gives a friendlier error message.
    const validationError = Object.keys(docTypes).reduce((acc, role) => {
      if (acc) return acc;
      const seen = new Set();
      const rows = docTypes[role] ?? [];
      return rows.reduce((rowErr, r, i) => {
        if (rowErr) return rowErr;
        if (!r.key || !r.key.trim()) return `${role}: row ${i + 1} is missing a key`;
        if (seen.has(r.key)) return `${role}: duplicate key "${r.key}"`;
        seen.add(r.key);
        if (!r.label || !r.label.trim()) return `${role}: row "${r.key}" is missing a label`;
        return null;
      }, null);
    }, null);
    if (validationError) {
      openMessage(validationError, timeoutWarning, typeWarning);
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'sites', siteId), {
        'siteDetail.configuration.rejectionReasons': reasons,
        'siteDetail.configuration.accountApplicationDocTypes': docTypes,
        'siteDetail.configuration.accountApplicationDocsRequired': docsRequired,
      });
      openMessage('Saved', timeoutSuccess, typeSuccess);
      setDirty(false);
    } catch (error) {
      console.error('-- Error saving account-application config:', error);
      openMessage(`Error saving: ${error.message}`, timeoutError, typeError);
    } finally {
      setSaving(false);
    }
  };

  // ───────── docTypes editor helpers ─────────
  const currentDocs = docTypes[selectedRole] ?? [];
  const requiredFlag = docsRequired[selectedRole] !== false; // default true

  const updateDocsForRole = (next) => {
    setDocTypes({ ...docTypes, [selectedRole]: next });
    setDirty(true);
  };

  const handleAddDocRow = () => {
    updateDocsForRole([...currentDocs, { key: '', label: '', required: false }]);
  };

  const handleEditDocRow = (index, field, value) => {
    const next = currentDocs.map((r, i) => (i === index ? { ...r, [field]: value } : r));
    updateDocsForRole(next);
  };

  const handleRemoveDocRow = (index) => {
    updateDocsForRole(currentDocs.filter((_, i) => i !== index));
  };

  const handleUseDefaults = () => {
    updateDocsForRole(DEFAULT_DOC_TYPES[selectedRole] ?? []);
  };

  const handleToggleRequiredFlag = (checked) => {
    setDocsRequired({ ...docsRequired, [selectedRole]: checked });
    setDirty(true);
  };

  const docTypeColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      width: 200,
      render: (val, _row, index) => (
        <Input
          maxLength={50}
          onChange={(e) => handleEditDocRow(index, 'key', e.target.value.trim())}
          placeholder="propertyTitle"
          value={val}
        />
      ),
    },
    {
      title: 'Label (shown to applicant)',
      dataIndex: 'label',
      render: (val, _row, index) => (
        <Input
          maxLength={120}
          onChange={(e) => handleEditDocRow(index, 'label', e.target.value)}
          placeholder="Property title"
          value={val}
        />
      ),
    },
    {
      title: 'Required',
      dataIndex: 'required',
      width: 110,
      render: (val, _row, index) => (
        <Switch
          checked={!!val}
          onChange={(checked) => handleEditDocRow(index, 'required', checked)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, _row, index) => (
        <Popconfirm
          cancelText="Cancel"
          okText="Remove"
          okType="danger"
          onConfirm={() => handleRemoveDocRow(index)}
          title="Remove this document type?"
        >
          <Button danger icon={<DeleteOutlined />} size="small" type="link" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Title level={5} style={{ marginTop: 0 }}>Required documents per applicant role</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Stored at <code>sites/{siteId}.siteDetail.configuration.accountApplicationDocTypes</code>.
        The mobile NewAccountFlow renders a doc-upload slot for each entry;
        the BE rejects submissions missing any row marked <strong>Required</strong>.
        If a role has no entries the BE falls back to its built-in defaults
        — use &quot;Use defaults&quot; to seed and then customise.
      </Text>

      <Flex align="center" gap={12} wrap style={{ marginBottom: 12 }}>
        <Segmented
          onChange={setSelectedRole}
          options={ROLES}
          value={selectedRole}
        />
        <Flex align="center" gap={8}>
          <Text>Documents required for this role:</Text>
          <Switch checked={requiredFlag} onChange={handleToggleRequiredFlag} />
        </Flex>
      </Flex>

      {!requiredFlag && (
        <Alert
          message="Documents are optional for this role — the BE will accept submissions with an empty documents map."
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
        />
      )}

      <Table
        bordered
        columns={docTypeColumns}
        dataSource={currentDocs.map((row, idx) => ({ ...row, _idx: idx }))}
        loading={loading}
        locale={{
          emptyText: `No document types configured for ${selectedRole}. BE will use its built-in defaults.`,
        }}
        pagination={false}
        rowKey={(_, idx) => `${selectedRole}-${idx}`}
        size="small"
      />

      <Flex gap={8} style={{ marginTop: 8 }} wrap>
        <Button icon={<PlusOutlined />} onClick={handleAddDocRow} type="dashed">
          Add row
        </Button>
        <Button onClick={handleUseDefaults}>
          Use defaults
        </Button>
      </Flex>

      <Divider />

      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 15 }}>Canned rejection reasons</Text>
        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
          When you reject an account application, you can pick one of these
          reasons (free text still works). Applicants see this in the
          rejection email.
        </Text>
      </div>

      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Input
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={handleAdd}
          placeholder="e.g., Block/unit does not match our records"
          value={draft}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          type="primary"
        >
          Add
        </Button>
      </Space.Compact>

      <List
        bordered
        dataSource={reasons}
        loading={loading}
        locale={{ emptyText: 'No reasons configured. Add one above.' }}
        renderItem={(item, index) => (
          <List.Item
            actions={[
              <Popconfirm
                cancelText="Cancel"
                key="remove"
                okText="Remove"
                okType="danger"
                onConfirm={() => handleRemove(index)}
                title="Remove this reason?"
              >
                <Button danger icon={<DeleteOutlined />} type="link">
                  Remove
                </Button>
              </Popconfirm>,
            ]}
          >
            {item}
          </List.Item>
        )}
      />

      <Flex justify="flex-end" style={{ marginTop: 16 }}>
        <Button
          disabled={!dirty || saving}
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          type="primary"
        >
          Save Changes
        </Button>
      </Flex>
    </>
  );
}

SiteConfigAccountApplication.propTypes = {
  siteId: PropTypes.string.isRequired,
};
